import { Component, OnDestroy, OnInit } from '@angular/core';
import { HubConnectionState } from '@microsoft/signalr';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { SocketsManagerService } from '../../services';
import { WebSocketErrorType, WebSocketState } from '../../models';
import { CoreFacade } from 'src/app/stores/core/facades/core.facade';
import { LiveDataRefreshEnum } from 'src/app/settings/models/live-data-refresh.enum';

@Component({
  standalone: false,
  selector: 'app-sockets-manager',
  templateUrl: './sockets-manager.component.html',
  styleUrls: ['./sockets-manager.component.scss']
})
export class SocketsManagerComponent implements OnInit, OnDestroy {
  public webSockets$!: Observable<WebSocketState[]>;
  public connectionStateColors!: Map<HubConnectionState, string>;
  public HubConnectionState = HubConnectionState;
  public isRequestResponse = false;

  private _liveDataRefreshEnum = LiveDataRefreshEnum;
  private _subscriptions: Subscription = new Subscription();
  
  constructor(
    private readonly socketsManagerService: SocketsManagerService,
    private _coreFacade: CoreFacade,
  ) { }

  public ngOnInit(): void {
    const isRequestResponse = this._coreFacade.liveDataRefresh() === this._liveDataRefreshEnum.RequestResponse;
    this.isRequestResponse = isRequestResponse;
    if (!isRequestResponse) {
      this.setWebSockets$();
      this.setConnectionStateColors();
    }
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
  
  public async restartSocket(webSocketState: WebSocketState): Promise<void> {
    try {
      await webSocketState.hubConnection.stop();
      await webSocketState.hubConnection.start(); 
    } catch (error) {}
  }
  
  private setWebSockets$(): void {
    this.webSockets$ = this.socketsManagerService.getSockets$().pipe(
      map((sockets: WebSocketState[]) => {
        return sockets.map((socket: WebSocketState) => {
          socket.hubConnection.onclose((error?: Error) => this.handleCloseConnection(socket, error));
          socket.hubConnection.onreconnected((connectionId?: string) => this.handleReconnect(socket));
          socket.hubConnection.onreconnecting((error?: Error) => this.handleReconnecting(socket, error));

          const name: string = socket.name.split('/')[socket.name.split('/').length - 1]?.toLowerCase();
          
          return {
            ...socket,
            name
          };
        });
      })
    );
  }
  
  private handleCloseConnection(socket: WebSocketState, error?: Error): void {
    this.socketsManagerService.addError({
      error,
      errorType: WebSocketErrorType.CONNECTION_CLOSED,
      webSocketState: socket
    });
  }
  
  private handleReconnect(socket: WebSocketState): void {
    this.socketsManagerService.removeError(socket.name);
  }
  
  private handleReconnecting(socket: WebSocketState, error?: Error): void {
    this.socketsManagerService.addError({
      error,
      errorType: WebSocketErrorType.CONNECTION_RECONNECTING,
      webSocketState: socket
    });
  }
  
  private setConnectionStateColors(): void {
    this.connectionStateColors = new Map<HubConnectionState, string>()
      .set(HubConnectionState.Disconnected, 'red')
      .set(HubConnectionState.Connecting, 'orange')
      .set(HubConnectionState.Connected, 'green')
      .set(HubConnectionState.Disconnecting, 'orange')
      .set(HubConnectionState.Reconnecting, 'blue');
  }
}
